import { useState } from "react";
import { toast } from "sonner";
import { SectionHeader } from "./SectionHeader";
import { submitAccommodation } from "@/lib/api";
import useReveal from "@/lib/useReveal";
import { useSection } from "@/lib/SiteContent";

const initial = {
  name: "",
  email: "",
  phone: "",
  guests: 1,
  guest_names: [],
  nights: 1,
  check_in: "",
  check_out: "",
  preferences: "",
  message: "",
};

export const Accommodation = () => {
  const { subtitle } = useSection("accommodation", {
    subtitle: "Trenger du et sted å sove? Fyll ut skjemaet, så hjelper vi deg med å finne et passende alternativ i nærheten.",
  });
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const ref = useReveal();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateGuests = (raw) => {
    const n = Math.max(1, Math.min(10, Number(raw) || 1));
    setForm((f) => {
      const cur = f.guest_names || [];
      const need = Math.max(0, n - 1);
      const next = cur.slice(0, need);
      while (next.length < need) next.push("");
      return { ...f, guests: n, guest_names: next };
    });
  };

  const updateGuestName = (idx, value) => {
    setForm((f) => {
      const next = [...(f.guest_names || [])];
      next[idx] = value;
      return { ...f, guest_names: next };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Vennligst skriv inn navnet ditt.");
      return;
    }
    setLoading(true);
    try {
      await submitAccommodation({
        ...form,
        guests: Number(form.guests) || 1,
        nights: Number(form.nights) || 1,
        guest_names: (form.guest_names || []).map((s) => s.trim()).filter(Boolean),
      });
      setSubmitted(true);
      toast.success("Forespørselen er sendt!");
    } catch (err) {
      console.error(err);
      toast.error("Noe gikk galt. Prøv igjen.");
    } finally {
      setLoading(false);
    }
  };

  const extras = Math.max(0, Number(form.guests || 1) - 1);

  return (
    <section
      id="overnatting"
      data-testid="accommodation-section"
      className="py-24 md:py-40 px-6 md:px-12 lg:px-24 bg-[#EAEDDC]/40"
    >
      <div className="max-w-3xl mx-auto">
        <SectionHeader
          eyebrow="Kapittel VII"
          title="Forespørre overnatting"
          subtitle={subtitle}
          testId="accommodation"
        />

        {submitted ? (
          <div
            ref={ref}
            data-testid="accommodation-success"
            className="reveal text-center border border-[#68919B80] py-16 px-8 bg-[#F3F5E7]"
          >
            <p className="font-serif text-3xl md:text-4xl text-[#3D5E68] mb-6">
              Takk!
            </p>
            <p className="text-[#5A7682] font-light leading-relaxed max-w-lg mx-auto">
              Vi har mottatt forespørselen din og kommer tilbake til deg så fort
              vi kan.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setForm(initial);
              }}
              data-testid="accommodation-reset-button"
              className="mt-10 px-8 py-3 border border-[#3D5E68] text-xs uppercase tracking-[0.25em] text-[#3D5E68] hover:bg-[#3D5E68] hover:text-[#F3F5E7] transition-colors duration-500"
            >
              Send en ny forespørsel
            </button>
          </div>
        ) : (
          <form
            ref={ref}
            onSubmit={onSubmit}
            data-testid="accommodation-form"
            className="reveal space-y-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#68919B] mb-3">
                  Navn
                </label>
                <input
                  data-testid="accom-name-input"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="input-line"
                  placeholder="Ditt fulle navn"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#68919B] mb-3">
                  E-post
                </label>
                <input
                  data-testid="accom-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="input-line"
                  placeholder="navn@email.no"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#68919B] mb-3">
                  Telefon
                </label>
                <input
                  data-testid="accom-phone-input"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="input-line"
                  placeholder="+47"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#68919B] mb-3">
                  Antall personer
                </label>
                <input
                  data-testid="accom-guests-input"
                  type="number"
                  min="1"
                  max="10"
                  value={form.guests}
                  onChange={(e) => updateGuests(e.target.value)}
                  className="input-line"
                />
              </div>
            </div>

            {extras > 0 && (
              <div className="space-y-6">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#68919B]">
                  Navn på øvrige gjester
                </p>
                {Array.from({ length: extras }).map((_, i) => (
                  <div key={i}>
                    <label className="block text-[10px] uppercase tracking-[0.3em] text-[#68919B] mb-3">
                      Gjest {i + 2}
                    </label>
                    <input
                      data-testid={`accom-guest-name-${i}`}
                      type="text"
                      value={(form.guest_names || [])[i] || ""}
                      onChange={(e) => updateGuestName(i, e.target.value)}
                      className="input-line"
                      placeholder="Fullt navn"
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#68919B] mb-3">
                  Innsjekk
                </label>
                <input
                  data-testid="accom-checkin-input"
                  type="date"
                  value={form.check_in}
                  onChange={(e) => update("check_in", e.target.value)}
                  className="input-line"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#68919B] mb-3">
                  Utsjekk
                </label>
                <input
                  data-testid="accom-checkout-input"
                  type="date"
                  value={form.check_out}
                  onChange={(e) => update("check_out", e.target.value)}
                  className="input-line"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-[#68919B] mb-3">
                  Antall netter
                </label>
                <input
                  data-testid="accom-nights-input"
                  type="number"
                  min="1"
                  max="14"
                  value={form.nights}
                  onChange={(e) => update("nights", e.target.value)}
                  className="input-line"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-[0.3em] text-[#68919B] mb-3">
                Preferanser
              </label>
              <input
                data-testid="accom-preferences-input"
                type="text"
                value={form.preferences}
                onChange={(e) => update("preferences", e.target.value)}
                className="input-line"
                placeholder="F.eks. hytte, hotell, gangavstand til lokalet..."
              />
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-[0.3em] text-[#68919B] mb-3">
                Melding (valgfritt)
              </label>
              <textarea
                data-testid="accom-message-input"
                rows="3"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="input-line resize-none"
                placeholder="Andre ønsker eller spørsmål..."
              />
            </div>

            <div className="pt-6">
              <button
                type="submit"
                disabled={loading}
                data-testid="accom-submit-button"
                className="w-full md:w-auto px-12 py-4 bg-[#68919B] text-[#F3F5E7] text-xs uppercase tracking-[0.3em] hover:bg-[#3D5E68] transition-colors duration-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Sender…" : "Send forespørsel"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};
